import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  FormRow,
  Input
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';

import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../utils/joiConfig';

interface RenameZustaendigeStelleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  zustaendigeStelle: {
    appiid: number;
    dispname: string;
  };
}

type FormData = {
  name: string;
};

export function RenameZustaendigeStelleModal({
  isOpen,
  onClose,
  onSuccess,
  zustaendigeStelle
}: RenameZustaendigeStelleModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const CustomJoi = createJoiWithTranslations(t);

  const { control, handleSubmit, reset, formState, trigger } =
    useForm<FormData>({
      defaultValues: {
        name: zustaendigeStelle.dispname
      },
      resolver: joiResolver(
        CustomJoi.object<FormData>({
          name: CustomJoi.string()
            .required()
            .messages({
              'string.empty': t(
                'RenameZustaendigeStelleModal.validation.nameRequired'
              )
            })
        })
      ),
      mode: 'onTouched'
    });

  const { mutateAsync: renameZustaendigeStelle } = useMutation({
    mutationFn: async (data: FormData) => {
      const result = await notifyUserFromRequest(
        () =>
          govosClient.put(
            GovosApiRoutes.zustaendigeStelle(zustaendigeStelle.appiid),
            {
              dispname: data.name
            }
          ),
        {
          successMessage: t('api.success.renameZustaendigeStelle'),
          errorMessage: t('api.error.renameZustaendigeStelle'),
          loadingMessage: t('api.loading.renameZustaendigeStelle')
        }
      );
      return result;
    },
    onSuccess: () => {
      queryClient
        .refetchQueries({
          queryKey: [GovosApiRoutes.startApps()]
        })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: [
              GovosApiRoutes.zustaendigeStelle(zustaendigeStelle.appiid)
            ]
          });
        })
        .then(() => {
          onSuccess();
          reset();
        });
    }
  });

  const handleRename = async () => {
    const isValid = await trigger();
    if (isValid) {
      handleSubmit((data) => {
        renameZustaendigeStelle(data);
      })();
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('RenameZustaendigeStelleModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <FormRow
          label={t('RenameZustaendigeStelleModal.nameLabel')}
          validationMessage={formState.errors.name?.message}
          variant={formState.errors.name ? 'error' : undefined}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={t('RenameZustaendigeStelleModal.namePlaceholder')}
              />
            )}
          />
        </FormRow>
      </Modal.Content>
      <Modal.Footer>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: '100%'
          }}
        >
          <Button
            variant="primary"
            onClick={() => void handleRename()}
            disabled={!formState.isValid}
            style={{ width: '100%' }}
          >
            {t('RenameZustaendigeStelleModal.saveButton')}
          </Button>
          <Button
            variant="secondary-outline"
            onClick={handleClose}
            style={{ width: '100%' }}
          >
            {t('RenameZustaendigeStelleModal.cancelButton')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
